import type { MeResponse } from '@/api/cmApi'
import { NAV, type NavGroup } from './nav'

/** Owner CM accounts (no staffUserId) see everything. */
export function isStaffCaller(caller: MeResponse['caller'] | undefined): boolean {
  return Boolean(caller?.staffUserId)
}

/** Bet Ticker page scope for Access Level grants. */
export type TickerAccessPage = 'live' | 'previous' | 'flag-final' | 'betfair' | 'master'

function tickerGrantPrefix(page: TickerAccessPage): string {
  if (page === 'flag-final') return 'bet-ticker-flag-final'
  if (page === 'previous') return 'bet-ticker-previous'
  if (page === 'betfair') return 'betfair-bet-ticker'
  if (page === 'master') return 'master-bet-ticker'
  return 'bet-ticker'
}

/** Map legacy `previous?: boolean` call sites → page scope. */
export function tickerAccessPage(previousOrPage: boolean | TickerAccessPage = 'live'): TickerAccessPage {
  if (previousOrPage === true || previousOrPage === 'previous') return 'previous'
  if (previousOrPage === 'flag-final') return 'flag-final'
  if (previousOrPage === 'betfair') return 'betfair'
  if (previousOrPage === 'master') return 'master'
  return 'live'
}

/**
 * Bet Ticker / Previous / Flag Final Smart Members — page-scoped.
 * Legacy `ticker.smart-members` still counts until staff Access Level is re-saved.
 */
export function canManageSmartMembers(
  caller: MeResponse['caller'] | undefined,
  previousOrPage: boolean | TickerAccessPage = 'live',
): boolean {
  if (!isStaffCaller(caller)) return true
  const scope = tickerAccessPage(previousOrPage)
  // Lab: Smart Members sits behind CM `isFlag` on the shared bet-ticker template
  // (also used when CM opens Master Bet Ticker). Reuse Bet Ticker smart grants.
  if (scope === 'betfair' || scope === 'master') {
    return hasAnyGrant(caller?.grants, ['bet-ticker.smart-members', 'ticker.smart-members'])
  }
  const page = `${tickerGrantPrefix(scope)}.smart-members`
  return hasAnyGrant(caller?.grants, [page, 'ticker.smart-members'])
}

/**
 * PL Statement Invalid Bets / Invalidate P&L — CM owner always;
 * CM staff need invalidate-pnl or invalid-bet (Lab CM Access Level).
 */
export function canInvalidatePlSettlement(
  caller: MeResponse['caller'] | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['invalidate-pnl', 'invalid-bet'])
}

/**
 * Bet Ticker / Previous / Flag Final Members Name (Member's mapping) — page-scoped.
 * Legacy cheater-members codes still count until Access Level re-save.
 */
export function canManageMembersMapping(
  caller: MeResponse['caller'] | undefined,
  previousOrPage: boolean | TickerAccessPage = 'live',
): boolean {
  if (!isStaffCaller(caller)) return true
  const scope = tickerAccessPage(previousOrPage)
  // Lab Master Bet Ticker: Members Name mapping (Lab Master parity; reuse Bet Ticker codes).
  if (scope === 'master' || scope === 'betfair') {
    return hasAnyGrant(caller?.grants, [
      'bet-ticker.members-mapping',
      'bet-ticker.cheater-members',
      'ticker.cheater-members',
    ])
  }
  const prefix = tickerGrantPrefix(scope)
  return hasAnyGrant(caller?.grants, [
    `${prefix}.members-mapping`,
    `${prefix}.cheater-members`,
    'ticker.cheater-members',
  ])
}

/**
 * Bet Ticker / Previous / Flag Final Members Ip (Member's IP mapping) — page-scoped.
 * Legacy cheater-members codes still count until Access Level re-save.
 */
export function canManageMembersIpMapping(
  caller: MeResponse['caller'] | undefined,
  previousOrPage: boolean | TickerAccessPage = 'live',
): boolean {
  if (!isStaffCaller(caller)) return true
  const scope = tickerAccessPage(previousOrPage)
  // Lab Master Bet Ticker: Members Ip mapping (Lab Master parity; reuse Bet Ticker codes).
  if (scope === 'master' || scope === 'betfair') {
    return hasAnyGrant(caller?.grants, [
      'bet-ticker.members-ip-mapping',
      'bet-ticker.cheater-members',
      'ticker.cheater-members',
    ])
  }
  const prefix = tickerGrantPrefix(scope)
  return hasAnyGrant(caller?.grants, [
    `${prefix}.members-ip-mapping`,
    `${prefix}.cheater-members`,
    'ticker.cheater-members',
  ])
}

/** @deprecated Use canManageMembersMapping / canManageMembersIpMapping. */
export function canManageCheaterMembers(
  caller: MeResponse['caller'] | undefined,
  previousOrPage: boolean | TickerAccessPage = 'live',
): boolean {
  return (
    canManageMembersMapping(caller, previousOrPage) ||
    canManageMembersIpMapping(caller, previousOrPage)
  )
}

/**
 * Bet Ticker / Previous / Flag Final flag (and unflag) — page-scoped.
 * Legacy `ticker.flag` still counts until staff Access Level is re-saved.
 */
export function canFlagTickerBet(
  caller: MeResponse['caller'] | undefined,
  previousOrPage: boolean | TickerAccessPage = 'live',
): boolean {
  if (!isStaffCaller(caller)) return true
  const page = `${tickerGrantPrefix(tickerAccessPage(previousOrPage))}.flag`
  return hasAnyGrant(caller?.grants, [page, 'ticker.flag'])
}

/**
 * Bet Ticker / Previous / Flag Final comment — page-scoped.
 * Legacy `ticker.comment` still counts until staff Access Level is re-saved.
 */
export function canCommentTickerBet(
  caller: MeResponse['caller'] | undefined,
  previousOrPage: boolean | TickerAccessPage = 'live',
): boolean {
  if (!isStaffCaller(caller)) return true
  const page = `${tickerGrantPrefix(tickerAccessPage(previousOrPage))}.comment`
  return hasAnyGrant(caller?.grants, [page, 'ticker.comment'])
}

/**
 * Bet Ticker Flag Final Generate / Download Excel.
 * Lab Access Level code `ticker.export` (Generate Excel + Download Excel).
 */
export function canExportTickerExcel(
  caller: MeResponse['caller'] | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['ticker.export'])
}

export function hasAnyGrant(grants: string[] | undefined, codes: string[]): boolean {
  if (!codes.length) return true
  const set = new Set(grants ?? [])
  return codes.some((c) => set.has(c))
}

/** Nav item id → any-of grants. Missing entry = always visible. */
const NAV_ITEM_GRANTS: Record<string, string[]> = {
  'net-exposure': ['books.view'],
  'betfair-net-exposure': ['betfair-net-exposure.view'],
  'betfair-balance-dashboard': ['betfair-balance.view'],
  // Lab permissions seed: Betfair / Master Bet Ticker list = bet-ticker.view
  'betfair-bet-ticker': ['bet-ticker.view'],
  'master-bet-ticker': ['bet-ticker.view'],
  'bet-ticker': ['bet-ticker.view'],
  'bet-ticker-previous': ['bet-ticker-previous.view'],
  // Merged into Previous — GrantGate allows path so <Navigate> can run.
  'bet-ticker-previous-new': ['bet-ticker-previous.view'],
  'bet-ticker-flag-final': ['bet-ticker-flag-final.view'],
  'portal-hierarchy': ['downline.view'],
  'downline-list': ['downline.view'],
  'hierarchy-search': ['hierarchy-search.view'],
  'create-downline': ['create-downline'],
  'user-access': ['staff.view', 'staff.write', 'staff.create'],
  'manage-access-roles': ['staff.write'],
  'aggregate-members': [
    'aggregate-members.view',
    'aggregate-members.create',
    'aggregate-members.edit',
  ],
  home: ['catalog.view'],
  manual: ['catalog.view', 'catalog.fetch'],
  browse: ['catalog.view'],
  automation: ['catalog.view', 'catalog.automation'],
  // Page-scoped — Catalog / Automation codes must not unlock Betfair Market Fetch.
  'betfair-fetch': [
    'betfair-fetch.view',
    'betfair-fetch.fetch',
    'betfair-fetch.start-time',
  ],
  // Any Market Selection checkbox opens the page; actions are gated separately.
  'market-selection': [
    'market.view',
    'market.enable',
    'market.edit-events',
    'market.display-order',
    'market.write',
  ],
  // Lab Account Statement — page-scoped (Turn Over / My P/L must not unlock PL Statement).
  'my-pnl': ['my-pnl.view'],
  'pl-statement': ['player-pnl.view'],
  'transfer-statement': ['transfer.view'],
  'commission-report': ['commission.write'],
  // Merged /surveillance — New or Beta grants unlock the single page.
  'surveillance-live': [
    'surveillance-new.view',
    'surveillance-new.clear-members',
    'surveillance-new.generate-graph',
    'surveillance-beta.view',
    'surveillance-beta.clear-members',
    'surveillance-beta.clear-date',
    'surveillance-beta.generate-graph',
  ],
  'surveillance-beta': [
    'surveillance-beta.view',
    'surveillance-beta.clear-members',
    'surveillance-beta.clear-date',
    'surveillance-beta.generate-graph',
  ],
  // Do not OR vendor.view — Vendor Dashboard pages are page-scoped separately.
  'card-game': ['card-game.view'],
  'live-casino': ['live-casino.view'],
  betfair: ['betfair-dashboard.view'],
  'instant-games': ['instant-games.view'],
  // Do not OR bet-settings.view — Max Limits / Volume Percent are page-scoped.
  'max-limits': ['max-limits.view', 'max-limits.edit', 'max-limits.save-all'],
  'max-limit-logs': ['max-limits.view', 'max-limits.edit', 'max-limits.save-all'],
  'volume-percent': [
    'volume-percent-new.view',
    'volume-percent.write',
    'volume-percent-new.update-rate',
  ],
  'betfair-rates-logs': ['betfair-rates-logs.view', 'betfair-rates-logs.view-ip'],
  'invalid-bets': ['invalid-bets.list', 'invalid-bets.view-detail'],
  // CL Transfers — match Backend-UI / CM-API; legacy logs.view still OK.
  'logs-cl-transfers': ['cl-transfers.view', 'logs.view'],
  'manage-ip': ['blocked-ip.view', 'blocked-ip.view-ip', 'blocked-ip.add', 'blocked-ip.delete'],
  'cm-ip-whitelist': ['security.cm-ip.list'],
  // Do not OR security.view — Security list pages are page-scoped separately.
  'blocked-ip': ['blocked-ip.view', 'blocked-ip.view-ip', 'blocked-ip.add', 'blocked-ip.delete'],
  'login-from-blocked-ip': ['login-from-blocked-ip.view', 'login-from-blocked-ip.view-ip'],
  'whitelist-users': [
    'whitelist-users.view',
    'whitelist-users.add-ip',
    'whitelist-users.add-device',
    'whitelist-users.add-ip-by-username',
    'whitelist-users.add-device-by-username',
    'whitelist-users.toggle',
  ],
  'blocked-device-ids': ['blocked-device-ids.add', 'blocked-device-ids.delete'],
  'all-cheaters': ['all-cheaters.view', 'all-cheaters.add', 'all-cheaters.toggle'],
  'odds-violation-whitelist': [
    'odds-violation-whitelist.view',
    'odds-violation-whitelist.add',
    'odds-violation-whitelist.remove',
  ],
  'manual-group-betting': [
    'aggregate-members.view',
    'aggregate-members.create',
    'aggregate-members.edit',
  ],
  'multiple-device-ids': ['multiple-device-ids.list'],
  'fprint-multiple-device-ids': [
    'fprint-multiple-device-ids.list',
    'fprint-multiple-device-ids.download',
    'fprint-multiple-device-ids.view-member',
  ],
  // Owner CM only — code never granted to staff Access Level users.
  'console-2fa': ['__cm_owner_only__'],
  // Access Level — Change Password (Lab long codes still authorize until re-save).
  'change-password': [
    'change-password.view',
    'change.password.change.password',
    'change.password.save.cancel',
  ],
}

/** Route path prefix → any-of grants. null = unrestricted. */
export function requiredGrantsForUiPath(pathname: string): string[] | null {
  const path = pathname.split('?')[0] || ''
  if (path.startsWith('/change-password') || path.startsWith('/security/change-password')) {
    return [
      'change-password.view',
      'change.password.change.password',
      'change.password.save.cancel',
    ]
  }
  if (path.startsWith('/users/access-roles')) return ['staff.write']
  // List: list/edit/add. Create form = add (staff.create). Edit form = edit (staff.write).
  if (path === '/users') return ['staff.view', 'staff.write', 'staff.create']
  if (path === '/users/new') return ['staff.create']
  if (path.startsWith('/users/')) return ['staff.write']
  if (path.startsWith('/hierarchy-search')) return ['hierarchy-search.view']
  if (path.startsWith('/hierarchy/create')) return ['create-downline']
  // Edit form is its own grant — do not OR with downline.view.
  if (path.startsWith('/hierarchy/edit')) return ['edit-downline']
  // Same grant as Member Account Statement — downline.view alone must not unlock it.
  if (path.startsWith('/hierarchy/statement')) return ['player-pnl.view']
  // Portal tree + Downline list need View Downline — Edit / Give-Take alone must not open them.
  if (path.startsWith('/hierarchy')) return ['downline.view']
  if (path.startsWith('/aggregate-members')) {
    return ['aggregate-members.view', 'aggregate-members.create', 'aggregate-members.edit']
  }
  // Member 360 — downline browse + tab grants (tabs gate further inside the page).
  if (path.startsWith('/member')) {
    return [
      'downline.view',
      'edit-downline',
      'credit-transfer',
      'player-pnl.view',
      'bet-ticker.view',
      'books.view',
      'transfer.view',
      'commission.view',
      'commission.write',
    ]
  }
  if (path.startsWith('/betfair-balance-dashboard')) return ['betfair-balance.view']
  if (path.startsWith('/betfair-net-exposure')) return ['betfair-net-exposure.view']
  if (path.startsWith('/betfair-bet-ticker')) return ['bet-ticker.view']
  // Same grant as Bet Ticker so GrantGate allows <Navigate to="/bet-ticker"> (rail still hidden).
  if (path.startsWith('/master-bet-ticker')) return ['bet-ticker.view']
  if (path.startsWith('/bet-ticker-flag-final')) return ['bet-ticker-flag-final.view']
  if (path.startsWith('/bet-ticker-previous')) return ['bet-ticker-previous.view']
  if (path.startsWith('/bet-ticker')) return ['bet-ticker.view']
  // Detail Book / View Total sit under Net Exposure — books.view only.
  if (path.startsWith('/net-exposure')) return ['books.view']
  // Market Selection is page-scoped — catalog.view alone must not unlock it.
  // Bookmaker Selection redirects here; BM codes do not open this page.
  if (
    path.startsWith('/catalog/market-selection') ||
    path.startsWith('/catalog/bookmaker-selection')
  ) {
    return [
      'market.view',
      'market.enable',
      'market.edit-events',
      'market.display-order',
      'market.write',
    ]
  }
  // Betfair Market Fetch — page-scoped (Catalog / Automation must not unlock).
  if (path.startsWith('/catalog/betfair-fetch')) {
    return ['betfair-fetch.view', 'betfair-fetch.fetch', 'betfair-fetch.start-time']
  }
  if (path.startsWith('/catalog') || path === '/home') {
    return ['catalog.view', 'catalog.fetch', 'catalog.automation', 'market.view']
  }
  // My P&L / PL Statement / Commission Report are page-scoped (not Insights / Turn Over).
  if (path.startsWith('/reports/my-pnl') || path.startsWith('/master-dashboard-new')) {
    return ['my-pnl.view']
  }
  if (path.startsWith('/reports/pl-statement') || path.startsWith('/PLStatement')) {
    return ['player-pnl.view']
  }
  if (
    path.startsWith('/reports/transfer-statement') ||
    path.startsWith('/viewTransaction')
  ) {
    return ['transfer.view']
  }
  if (
    path.startsWith('/reports/commission-report') ||
    path.startsWith('/commission-report')
  ) {
    return ['commission.write']
  }
  if (path.startsWith('/reports')) {
    return ['player-pnl.view', 'my-pnl.view', 'transfer.view', 'commission.write']
  }
  // Merged page (+ legacy /surveillance/beta redirect).
  if (path.startsWith('/surveillance')) {
    return [
      'surveillance-new.view',
      'surveillance-new.clear-members',
      'surveillance-new.generate-graph',
      'surveillance-beta.view',
      'surveillance-beta.clear-members',
      'surveillance-beta.clear-date',
      'surveillance-beta.generate-graph',
    ]
  }
  // Vendor Dashboard — page-scoped (shared vendor.view must not unlock every page).
  if (path.startsWith('/vendor-dashboard/card-game')) return ['card-game.view']
  if (path.startsWith('/vendor-dashboard/live-casino')) return ['live-casino.view']
  if (path.startsWith('/vendor-dashboard/instant-games')) return ['instant-games.view']
  if (path.startsWith('/vendor-dashboard/betfair')) return ['betfair-dashboard.view']
  if (path.startsWith('/vendor-dashboard')) {
    return [
      'card-game.view',
      'live-casino.view',
      'instant-games.view',
      'betfair-dashboard.view',
      'premium-bookmaker.view',
      'premium-bookmaker.details',
    ]
  }
  // Max Limits / Volume Percent / All Logs under bet-settings — page-scoped.
  if (path.startsWith('/bet-settings/max-limits') || path.startsWith('/bet-settings/max-limit-logs')) {
    return ['max-limits.view', 'max-limits.edit', 'max-limits.save-all']
  }
  if (path.startsWith('/bet-settings/volume-percent')) {
    return [
      'volume-percent-new.view',
      'volume-percent.write',
      'volume-percent-new.update-rate',
    ]
  }
  if (path.startsWith('/logs/cl-transfers')) {
    return ['cl-transfers.view', 'logs.view']
  }
  // Old /bet-settings/* aliases — GrantGate runs before <Navigate>; keep both prefixes.
  if (
    path.startsWith('/logs/betfair-rates-logs') ||
    path.startsWith('/bet-settings/betfair-rates-logs')
  ) {
    return ['betfair-rates-logs.view', 'betfair-rates-logs.view-ip']
  }
  if (path.startsWith('/logs/invalid-bets') || path.startsWith('/bet-settings/invalid-bets')) {
    return ['invalid-bets.list', 'invalid-bets.view-detail']
  }
  if (path.startsWith('/logs')) {
    // Unmapped logs path: deny staff (do not OR shared logs.view alone).
    return []
  }
  if (path.startsWith('/bet-settings')) {
    return [
      'max-limits.view',
      'max-limits.edit',
      'max-limits.save-all',
      'volume-percent-new.view',
      'volume-percent.write',
      'volume-percent-new.update-rate',
    ]
  }
  // CM IP whitelist — list grant opens page (search included).
  if (path.startsWith('/security/ip-whitelist')) return ['security.cm-ip.list']
  // Console 2FA — owner CM only (staff never receive __cm_owner_only__).
  if (path.startsWith('/security/console-2fa')) return ['__cm_owner_only__']
  if (path.startsWith('/security/multiple-device-ids')) return ['multiple-device-ids.list']
  if (path.startsWith('/security/fprint-multiple-device-ids')) {
    return [
      'fprint-multiple-device-ids.list',
      'fprint-multiple-device-ids.download',
      'fprint-multiple-device-ids.view-member',
    ]
  }
  // Security lists — page-scoped (shared security.view must not unlock every page).
  if (path.startsWith('/security/manage-ip') || path.startsWith('/security/blocked-ip')) {
    return ['blocked-ip.view', 'blocked-ip.view-ip', 'blocked-ip.add', 'blocked-ip.delete']
  }
  if (path.startsWith('/security/login-from-blocked-ip')) {
    return ['login-from-blocked-ip.view', 'login-from-blocked-ip.view-ip']
  }
  if (path.startsWith('/security/whitelist-users')) {
    return [
      'whitelist-users.view',
      'whitelist-users.add-ip',
      'whitelist-users.add-device',
      'whitelist-users.add-ip-by-username',
      'whitelist-users.add-device-by-username',
      'whitelist-users.toggle',
    ]
  }
  if (path.startsWith('/security/blocked-device-ids')) {
    return ['blocked-device-ids.add', 'blocked-device-ids.delete']
  }
  if (path.startsWith('/security/all-cheaters')) {
    return ['all-cheaters.view', 'all-cheaters.add', 'all-cheaters.toggle']
  }
  if (path.startsWith('/security/odds-violation-whitelist')) {
    return [
      'odds-violation-whitelist.view',
      'odds-violation-whitelist.add',
      'odds-violation-whitelist.remove',
    ]
  }
  if (path.startsWith('/security/manual-group-betting')) {
    return ['aggregate-members.view', 'aggregate-members.create', 'aggregate-members.edit']
  }
  if (path.startsWith('/security')) {
    // Unmapped security path: deny staff (do not OR shared security.view).
    return []
  }
  return null
}

export function canAccessUiPath(
  caller: MeResponse['caller'] | undefined,
  pathname: string,
): boolean {
  if (!isStaffCaller(caller)) return true
  const path = pathname.split('?')[0] || ''
  // Console 2FA is owner-CM only — never openable via staff grants (incl. forged codes).
  if (path.startsWith('/security/console-2fa')) return false
  const needed = requiredGrantsForUiPath(pathname)
  if (needed == null) return true
  // `return []` from requiredGrantsForUiPath means deny (unmapped section path).
  if (needed.length === 0) return false
  return hasAnyGrant(caller?.grants, needed)
}

export function filterNavByGrants(
  groups: NavGroup[] = NAV,
  caller: MeResponse['caller'] | undefined,
): NavGroup[] {
  const withoutHidden = groups
    .map((g) => ({
      ...g,
      items: g.items.filter((item) => !item.hiddenFromNav),
    }))
    .filter((g) => g.items.length > 0)
  if (!isStaffCaller(caller)) return withoutHidden
  const grants = caller?.grants
  return withoutHidden
    .map((g) => ({
      ...g,
      items: g.items.filter((item) => {
        if (item.id === 'console-2fa') return false
        const needed = NAV_ITEM_GRANTS[item.id]
        if (!needed) return true
        return hasAnyGrant(grants, needed)
      }),
    }))
    .filter((g) => g.items.length > 0)
}

/** First rail path staff may open (post-login falldown). */
export function firstAccessiblePath(
  caller: MeResponse['caller'] | undefined,
  fallback = '/home',
): string {
  if (!isStaffCaller(caller)) return fallback
  for (const group of filterNavByGrants(NAV, caller)) {
    for (const item of group.items) {
      if (item.to && canAccessUiPath(caller, item.to)) return item.to
    }
  }
  return fallback
}

/** Events column match On/Off — Event Lock/Unlock. */
export function canProductLock(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['product-lock'])
}

/** Lock column modal (padlocks + sport On/Off) — Member Lock/Unlock only. */
export function canGameLock(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['lock'])
}

export function canGlobalLock(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['global-lock'])
}

export function canMemberLock(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['lock'])
}

/** Direct-child Edit / Edit downline — staff need edit-downline (owners bypass). */
export function canEditDownline(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['edit-downline'])
}

export function canCreateDownline(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['create-downline'])
}

/** Aggregate Members Create Group — owners bypass; staff need aggregate-members.create. */
export function canCreateAggregateMembers(
  caller: MeResponse['caller'] | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['aggregate-members.create'])
}

/** Aggregate Members Edit — owners bypass; staff need aggregate-members.edit. */
export function canEditAggregateMembers(
  caller: MeResponse['caller'] | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['aggregate-members.edit'])
}

export function canCreditTransfer(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['credit-transfer'])
}

export function canBulkTransfer(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['bulk-transfer'])
}

/** FP roleslists manage screen — owner or staff.write. */
export function canManageAccessRoles(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['staff.write'])
}

/** Edit staff users — edit User Access Level (staff.write). */
export function canEditStaffUsers(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['staff.write'])
}

/** Create staff users — add User Access Level (staff.create). */
export function canCreateStaffUsers(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['staff.create'])
}

/** Fancy / BM commission column — Lab "view commission Dashboard". */
export function canViewCommission(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['commission.view', 'commission.write'])
}

/** Member detail Balance + Login History — staff need downline.view (owners bypass). */
export function canViewDownline(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['downline.view'])
}

/** Member detail Bet List — Book Management → Bet Ticker → list bets. */
export function canViewBetList(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['bet-ticker.view'])
}

/** Member detail Net Exposure tab — Book Management → Net Exposure. */
export function canViewMemberNetExposure(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['books.view'])
}

/** Member detail Account Statement + Activity — Player P/L. */
export function canViewPlayerPnl(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['player-pnl.view'])
}

/** Member detail Transfer Statements. */
export function canViewTransferStatement(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['transfer.view'])
}

/**
 * Member detail tab visibility for Access Level staff.
 * Edit → edit-downline; Balance / Login History → downline.view;
 * Bet List → bet-ticker.view; Net Exposure → books.view;
 * Account Statement / Activity → player-pnl.view;
 * Transfer Statements → transfer.view; Commission → commission.view|write.
 */
export function staffCanSeeMemberDetailTab(
  tabId: string,
  caller: MeResponse['caller'] | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  if (tabId === 'edit') return canEditDownline(caller)
  if (tabId === 'balance' || tabId === 'login-history') return canViewDownline(caller)
  if (tabId === 'bets') return canViewBetList(caller)
  if (tabId === 'net-exposure') return canViewMemberNetExposure(caller)
  if (tabId === 'statement' || tabId === 'activity') return canViewPlayerPnl(caller)
  if (tabId === 'transfers') return canViewTransferStatement(caller)
  if (tabId === 'commission') return canViewCommission(caller)
  return true
}

/** Enable Market — market On/Off, limits, flags. market.write alone must not unlock this. */
export function canEnableMarket(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['market.enable'])
}

/** Edit Events — event On/Off. market.write / display-order must not unlock this. */
export function canEditMarketEvents(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['market.edit-events'])
}

/** Edit event display order — soccer sortOrder only. */
export function canEditMarketDisplayOrder(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['market.display-order'])
}

/** Bookmaker Selection Status Update — BM On/Off. Legacy bookmaker.write still counts. */
export function canEnableBookmaker(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['bookmaker.enable', 'bookmaker.write'])
}

/** Bookmaker Selection Edit Bookmaker Event — BM limits. Legacy bookmaker.write still counts. */
export function canEditBookmaker(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['bookmaker.edit', 'bookmaker.write'])
}

/** Manage IP / Blocked IP — Block action. Owners bypass. */
export function canBlockIp(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['blocked-ip.add'])
}

/** Odds Violation Whitelist — Add User. Owners bypass. */
export function canOddsViolationWhitelistAdd(
  caller: MeResponse['caller'] | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['odds-violation-whitelist.add'])
}

/** Odds Violation Whitelist — Remove. Owners bypass. */
export function canOddsViolationWhitelistRemove(
  caller: MeResponse['caller'] | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['odds-violation-whitelist.remove'])
}

/** Betfair Market Fetch — Fetch button. Legacy catalog.fetch still counts. */
export function canFetchBetfairMarkets(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['betfair-fetch.fetch', 'catalog.fetch'])
}

/** Betfair Market Fetch — Update start time. Legacy catalog.automation still counts. */
export function canUpdateBetfairStartTime(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['betfair-fetch.start-time', 'catalog.automation'])
}

/** Betfair Rates Logs — show IP column. */
export function canViewBetfairRatesLogIp(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['betfair-rates-logs.view-ip'])
}

/** Max Limits — edit field inputs. */
export function canEditMaxLimits(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['max-limits.edit'])
}

/** Max Limits — Save All. */
export function canSaveAllMaxLimits(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['max-limits.save-all'])
}

/** Volume Percent New — Update Rate & TMs. */
export function canUpdateVolumePercentRate(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['volume-percent-new.update-rate'])
}

/** Volume Percent New — Edit / Save TM rows. */
export function canEditVolumePercent(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['volume-percent.write'])
}

/** Surveillance New — Clear Members. */
export function canClearSurveillanceNewMembers(
  caller: MeResponse['caller'] | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['surveillance-new.clear-members'])
}

/** Surveillance New — Generate Graph. */
export function canGenerateSurveillanceNewGraph(
  caller: MeResponse['caller'] | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['surveillance-new.generate-graph'])
}

/** SurveillanceBeta — Clear Members. */
export function canClearSurveillanceBetaMembers(
  caller: MeResponse['caller'] | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['surveillance-beta.clear-members'])
}

/** SurveillanceBeta — Clear Date. */
export function canClearSurveillanceBetaDate(
  caller: MeResponse['caller'] | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['surveillance-beta.clear-date'])
}

/** SurveillanceBeta — Generate Graph. */
export function canGenerateSurveillanceBetaGraph(
  caller: MeResponse['caller'] | undefined,
): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['surveillance-beta.generate-graph'])
}

/** CM IP whitelist Add / update form — owners bypass. */
export function canAddCmIpWhitelist(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['security.cm-ip.add'])
}

/** CM IP whitelist Delete — owners bypass. */
export function canDeleteCmIpWhitelist(caller: MeResponse['caller'] | undefined): boolean {
  if (!isStaffCaller(caller)) return true
  return hasAnyGrant(caller?.grants, ['security.cm-ip.delete'])
}

